import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ebayItemSpecificSchema,
  type EbayItemSpecifics,
  type EbayCategoryPolicy,
  type EbaySellerHubSinglesCategory,
} from '@tcvs/shared';

import { CcgItemDetails } from './CcgItemDetails';

function normalizedValues(text: string, multiple: boolean): string[] {
  const parts = multiple ? text.split('|') : [text];
  return [...new Set(parts.map((value) => value.trim()).filter(Boolean))];
}

export function EbayItemDetailsModal({
  open,
  fieldName,
  category,
  value,
  disabled,
  onCategoryChange,
  onChange,
  onClose,
  policy,
}: {
  open: boolean;
  fieldName?: string;
  category: EbaySellerHubSinglesCategory | null;
  policy?: EbayCategoryPolicy;
  value: EbayItemSpecifics;
  disabled?: boolean;
  onCategoryChange: (category: EbaySellerHubSinglesCategory) => void;
  onChange: (value: EbayItemSpecifics) => void;
  onClose: () => void;
}): JSX.Element | null {
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);
  const fields = useMemo(() => {
    if (category === null || category === 'CCG_SINGLES') return [];
    if (fieldName !== undefined) {
      const needle = query.trim().toLocaleLowerCase('en-US');
      return ebayItemSpecificSchema(category)
        .filter((field) => field.name === fieldName)
        .map((field) => ({
          ...field,
          suggestedValues:
            needle === ''
              ? field.suggestedValues
              : field.suggestedValues.filter((item) =>
                  item.toLocaleLowerCase('en-US').includes(needle),
                ),
        }));
    }
    const needle = query.trim().toLocaleLowerCase('en-US');
    return ebayItemSpecificSchema(category).filter(
      (field) =>
        needle === '' ||
        field.name.toLocaleLowerCase('en-US').includes(needle) ||
        field.suggestedValues.some((item) => item.toLocaleLowerCase('en-US').includes(needle)),
    );
  }, [category, fieldName, query]);
  if (!open) return null;
  const setField = (name: string, values: string[]) => {
    const next = { ...value };
    if (values.length === 0 && name !== 'Features') delete next[name];
    else next[name] = values;
    onChange(next);
  };
  return createPortal(
    <div className="tcvs-modal-backdrop tcvs-item-details-backdrop" role="presentation">
      <section
        className="tcvs-modal tcvs-item-details-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tcvs-item-details-title"
      >
        <header>
          <div>
            <h3 id="tcvs-item-details-title">
              {fieldName === undefined ? 'eBay item details' : `eBay ${fieldName.toLowerCase()}`}
            </h3>
            <p className="tcvs-muted">
              {category === null
                ? 'Choose a listing category'
                : category === 'CCG_SINGLES'
                  ? 'Collectible card game single'
                  : category === 'SPORT_SINGLES'
                    ? 'Sport single'
                    : 'Non-sport single'}{' '}
              Seller Hub values.
            </p>
          </div>
          <button type="button" className="tcvs-btn-secondary" onClick={onClose}>
            Done
          </button>
        </header>
        {fieldName === undefined ? (
          <div className="tcvs-item-details-tools">
            <label>
              <span>Listing category</span>
              <select
                value={category ?? ''}
                disabled={disabled}
                onChange={(event) =>
                  onCategoryChange(event.target.value as EbaySellerHubSinglesCategory)
                }
              >
                <option value="" disabled>
                  Choose listing category
                </option>
                <option value="CCG_SINGLES">Collectible card game single</option>
                <option value="SPORT_SINGLES">Sport single</option>
                <option value="NON_SPORT_SINGLES">Non-sport single</option>
              </select>
            </label>
            <label>
              <span>Search item specifics</span>
              <input
                autoFocus
                type="search"
                value={query}
                placeholder="Features, team, language…"
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
          </div>
        ) : (
          <div className="tcvs-item-details-tools">
            <label>
              <span>Filter {fieldName.toLowerCase()}</span>
              <input
                autoFocus
                type="search"
                value={query}
                placeholder={`Search ${fieldName.toLowerCase()}`}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
          </div>
        )}
        {category === 'CCG_SINGLES' ? (
          policy ? (
            <CcgItemDetails
              policy={policy}
              value={value}
              onChange={onChange}
              disabled={disabled}
              fieldName={fieldName}
            />
          ) : (
            <p>Current CCG policy is required. Close this dialog and reload the policy.</p>
          )
        ) : category === null ? (
          <p>Choose a listing category to review item details.</p>
        ) : (
          <div className="tcvs-item-specific-list">
            {fields.map((field) => (
              <div key={field.name} className="tcvs-item-specific-field">
                <label>
                  <span>
                    {field.name} {field.required ? <strong aria-label="required">*</strong> : null}
                  </span>
                  <input
                    value={(value[field.name] ?? []).join(' | ')}
                    disabled={disabled}
                    placeholder={field.multiple ? 'Separate multiple values with |' : 'Optional'}
                    onChange={(event) =>
                      setField(field.name, normalizedValues(event.target.value, field.multiple))
                    }
                  />
                </label>
                {field.multiple && field.suggestedValues.length > 0 ? (
                  <div
                    className="tcvs-item-specific-checkboxes"
                    aria-label={`${field.name} values`}
                  >
                    {field.suggestedValues.map((suggestion) => {
                      const selected = value[field.name]?.includes(suggestion) ?? false;
                      return (
                        <label key={suggestion} className={selected ? 'is-selected' : ''}>
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={disabled}
                            onChange={() => {
                              const current = value[field.name] ?? [];
                              setField(
                                field.name,
                                selected
                                  ? current.filter((item) => item !== suggestion)
                                  : [...current, suggestion],
                              );
                            }}
                          />
                          <span>{suggestion}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : field.suggestedValues.length > 0 ? (
                  <div
                    className="tcvs-item-specific-suggestions"
                    aria-label={`${field.name} values`}
                  >
                    {field.suggestedValues.map((suggestion) => {
                      const selected = value[field.name]?.includes(suggestion) ?? false;
                      return (
                        <button
                          key={suggestion}
                          type="button"
                          className={selected ? 'is-selected' : ''}
                          aria-pressed={selected}
                          disabled={disabled}
                          onClick={() => {
                            const current = value[field.name] ?? [];
                            setField(
                              field.name,
                              selected
                                ? current.filter((item) => item !== suggestion)
                                : field.multiple
                                  ? [...current, suggestion]
                                  : [suggestion],
                            );
                          }}
                        >
                          {suggestion}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ))}
            {fields.length === 0 ? <p className="tcvs-muted">No matching item specifics.</p> : null}
          </div>
        )}
      </section>
    </div>,
    document.body,
  );
}
